import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './CompactCalendar.module.css';

const CompactCalendar = ({
  selectedDate,
  selectedDates = new Set(),
  onDateSelect,
  currentMonth,
  onMonthChange,
  enabledDates = new Set(),
  forceExpanded = false,
  dateSessionsMap = new Map(),
}) => {
  const [isExpanded, setIsExpanded] = useState(forceExpanded);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Get current date for comparison
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonthIndex = today.getMonth();
  const currentYear = today.getFullYear();

  // Generate calendar days
  const generateCalendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [currentMonth]);

  // Get the week containing the selected date or current date
  const getDisplayWeek = () => {
    const targetDay = selectedDate || currentDay;
    const allDays = generateCalendarDays;

    // Find the index of the target day
    const targetIndex = allDays.findIndex((day) => day === targetDay);

    if (targetIndex === -1) return allDays.slice(0, 7);

    // Find the start of the week containing the target day
    const startOfWeek = targetIndex - (targetIndex % 7);

    return allDays.slice(startOfWeek, startOfWeek + 7);
  };

  const displayDays = isExpanded ? generateCalendarDays : getDisplayWeek();

  const handleDateClick = (day) => {
    if (day && isDateEnabled(day)) {
      onDateSelect(day);
    }
  };

  // Check if a date is enabled
  const isDateEnabled = (day) => {
    if (!day || enabledDates.size === 0) return false; // If no enabled dates specified, all are enabled
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;
    return enabledDates.has(dateString);
  };

  const handlePreviousMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    onMonthChange(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    onMonthChange(newMonth);
  };

  const isToday = (day) => {
    return (
      day === currentDay &&
      currentMonth.getMonth() === currentMonthIndex &&
      currentMonth.getFullYear() === currentYear
    );
  };

  return (
    <div
      className={classNames(css.calendar, { [css.forceExpanded]: forceExpanded })}
      onMouseEnter={() => !forceExpanded && setIsExpanded(true)}
      onMouseLeave={() => !forceExpanded && setIsExpanded(false)}
    >
      {/* Calendar Header */}
      <div className={css.calendarHeader}>
        <span className={css.monthLabel}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <div className={css.navContainer}>
          <button type="button" className={css.monthNav} onClick={handlePreviousMonth}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button type="button" className={css.monthNav} onClick={handleNextMonth}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className={css.calendarGrid}>
        {/* Day headers */}
        {dayHeaders.map((day, index) => (
          <div key={`header-${index}`} className={css.dayHeader}>
            {day}
          </div>
        ))}

        {/* Days */}
        {displayDays.map((day, index) => {
          const enabled = isDateEnabled(day);

          // Check if this day is selected (either the primary selectedDate or in the selectedDates set)
          let isSelected = false;
          if (day) {
            if (day === selectedDate) {
              isSelected = true;
            } else {
              const year = currentMonth.getFullYear();
              const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
              const dayStr = String(day).padStart(2, '0');
              const dateString = `${year}-${month}-${dayStr}`;
              isSelected = selectedDates.has(dateString);
            }
          }

          return (
            <div
              key={`day-${index}`}
              className={classNames(css.calendarDay, {
                [css.emptyDay]: !day,
                [css.selectedDay]: isSelected,
                [css.today]: isToday(day),
                [css.disabledDay]: day && !enabled,
              })}
              onClick={() => handleDateClick(day)}
              style={{ cursor: day && enabled ? 'pointer' : 'default' }}
            >
              {day}
              {day &&
                enabled &&
                dateSessionsMap.size > 0 &&
                (() => {
                  const year = currentMonth.getFullYear();
                  const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
                  const dayStr = String(day).padStart(2, '0');
                  const dateString = `${year}-${month}-${dayStr}`;
                  const sessionInfo = dateSessionsMap.get(dateString);
                  if (sessionInfo) {
                    return <div className={css.sessionNameLabel}>{sessionInfo.displayName}</div>;
                  }
                  return null;
                })()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

CompactCalendar.propTypes = {
  selectedDate: PropTypes.number,
  selectedDates: PropTypes.instanceOf(Set),
  onDateSelect: PropTypes.func.isRequired,
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  enabledDates: PropTypes.instanceOf(Set),
  dateSessionsMap: PropTypes.instanceOf(Map),
};

export default CompactCalendar;
